// Goro review scene: cosmetic warning geometry only. Never used for collision.
import type { BossSnap } from '../../common/src/protocol';
import { BOSSES } from '../../common/src/data';
import { COLOR } from './theme';
import type { Graphics } from 'pixi.js';

/** Same phase-adjusted duration as server windup; read-only cosmetic progress. */
export function windupOf(b: BossSnap): number {
  const a = BOSSES.tiers[b.k].attacks[b.atk];
  return Math.max(.1, (a?.windup ?? 1) * (1 - b.ph * .12));
}

/** Fixed danger boundary, filling charge, single commit cue. No oscillating flashes. */
export function goroWarning(b: BossSnap, g: Graphics, comfort: number): boolean {
  if (b.k !== 'brute' || b.w > 0 || !b.atk || b.on) return false;
  const progress = Math.max(0, Math.min(1, 1 - b.at / windupOf(b)));
  const commit = b.at > 0 && b.at <= .1;
  const alpha = commit ? [ .7, .5, .32 ][comfort] ?? .32 : .7;
  const x = Math.round(b.x), y = Math.round(b.y);
  if (b.atk === 'smash') {
    for (const [zx, zy, radius, left] of b.z) {
      const cx = Math.round(zx), cy = Math.round(zy);
      const p = Math.max(0, Math.min(1, 1 - left / windupOf(b)));
      g.circle(cx, cy, radius).fill({ color: COLOR.danger, alpha: .06 })
        .stroke({ color: COLOR.danger, width: 2, alpha: .9 });
      g.circle(cx, cy, Math.round(radius * p)).fill({ color: COLOR.danger, alpha: .12 });
      // Four fixed inward hammer brackets distinguish smash from other blast circles.
      for (const [dx, dy] of [[1, 0], [0, 1], [-1, 0], [0, -1]]) {
        const px = cx + dx * (radius - 3), py = cy + dy * (radius - 3);
        g.moveTo(px - dy * 4, py + dx * 4).lineTo(px + dy * 4, py - dx * 4)
          .moveTo(px, py).lineTo(px - dx * 6, py - dy * 6)
          .stroke({ color: COLOR.text, width: 1, alpha: .8 });
      }
      if (commit) g.circle(cx, cy, radius - 2).stroke({ color: COLOR.text, width: 2, alpha });
    }
    return true;
  }
  if (b.atk !== 'shotgun') return false;
  const a = BOSSES.tiers.brute.attacks.shotgun;
  const radius = BOSSES.tiers.brute.radius;
  const reach = b.rg + radius, start = b.aa - (a.spread ?? .35), end = b.aa + (a.spread ?? .35);
  const fan = (distance: number) => g.moveTo(x, y)
    .lineTo(Math.round(x + Math.cos(start) * distance), Math.round(y + Math.sin(start) * distance))
    .arc(x, y, distance, start, end).closePath();
  fan(reach).fill({ color: COLOR.danger, alpha: .05 }).stroke({ color: COLOR.danger, width: 1, alpha: .85 });
  fan(Math.round(radius + (reach - radius) * progress)).fill({ color: COLOR.danger, alpha: .12 });
  // Six radial ticks identify the pellet fan without a flickering laser.
  for (let i = 0; i < (a.pellets ?? 6); i++) {
    const angle = start + (end - start) * i / ((a.pellets ?? 6) - 1);
    g.moveTo(Math.round(x + Math.cos(angle) * (reach - 5)), Math.round(y + Math.sin(angle) * (reach - 5)))
      .lineTo(Math.round(x + Math.cos(angle) * reach), Math.round(y + Math.sin(angle) * reach))
      .stroke({ color: COLOR.text, width: 1, alpha: .8 });
  }
  if (commit) g.arc(x, y, reach - 2, start, end).stroke({ color: COLOR.text, width: 2, alpha });
  return true;
}
